import { useState, useEffect, useCallback, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { streamService, PublicStreamData } from "../services/streamService";
import { SoloStreamingCardProps } from "../Components/solo-streaming-components/SoloStreamingCard";
import { BASE_URL } from "../services/streamApiClient";

// Derive socket URL from BASE_URL (remove /api suffix)
const SOCKET_URL = BASE_URL.replace("/api", "");

// Socket event types matching backend
enum StreamRoomEventType {
  JOIN_HOME_UPDATES = "join-home-updates",
  STREAM_LIST_UPDATE = "stream-list-update",
}

interface StreamListUpdate {
  type: "NEW_LIVE" | "STREAM_ENDED" | "STATUS_CHANGED";
  streamId: string;
  title?: string;
  username?: string;
  platforms?: Record<string, { liveStreamId?: string; streamUrl?: string }>;
  timestamp: string;
}

interface UseLiveStreamersReturn {
  liveStreams: SoloStreamingCardProps[];
  pastStreams: SoloStreamingCardProps[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Convert PublicStreamData from API to SoloStreamingCardProps for UI
 */
function mapToCardProps(stream: PublicStreamData): SoloStreamingCardProps {
  return {
    platform: stream.platform,
    streamUrl: stream.streamUrl,
    username: stream.username || stream.userId,
    avatarUrl: stream.avatarUrl || "",
    title: stream.title || "Untitled Stream",
    viewerCount: stream.viewerCount,
    isLive: stream.status === "live",
  };
}

/**
 * Hook for fetching and subscribing to live/past stream lists
 * Used by home page LiveSoloStreamers component
 */
export function useLiveStreamers(): UseLiveStreamersReturn {
  const [liveStreams, setLiveStreams] = useState<SoloStreamingCardProps[]>([]);
  const [pastStreams, setPastStreams] = useState<SoloStreamingCardProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Fetch streams from API
  const fetchStreams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [liveData, pastData] = await Promise.all([
        streamService.getLiveStreams(),
        streamService.getPastStreams(20, 0),
      ]);

      setLiveStreams(liveData.map(mapToCardProps));
      setPastStreams(pastData.map(mapToCardProps));
    } catch (err) {
      console.error("Failed to fetch streams:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch streams");
    } finally {
      setLoading(false);
    }
  }, []);

  // Setup socket connection for real-time updates
  useEffect(() => {
    // Connect to stream-room namespace
    const socket = io(`${SOCKET_URL}/stream-room`, {
      transports: ["websocket"],
      autoConnect: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log(
        "[useLiveStreamers] Socket connected, subscribing to home updates"
      );
      // Subscribe to stream list updates
      socket.emit(StreamRoomEventType.JOIN_HOME_UPDATES);
    });

    socket.on(
      StreamRoomEventType.STREAM_LIST_UPDATE,
      (data: StreamListUpdate) => {
        console.log("[useLiveStreamers] Stream list update:", data);

        if (data.type === "NEW_LIVE") {
          // Refresh to get the new stream with full data
          fetchStreams();
        } else if (data.type === "STREAM_ENDED") {
          // Remove from live streams
          setLiveStreams((prev) =>
            prev.filter((s) => s.username !== data.username)
          );
          // Refresh past to include the ended stream
          streamService
            .getPastStreams(20, 0)
            .then((pastData) => {
              setPastStreams(pastData.map(mapToCardProps));
            })
            .catch(console.error);
        }
      }
    );

    socket.on("connect_error", (err) => {
      console.error("[useLiveStreamers] Socket connection error:", err);
    });

    // Initial fetch
    fetchStreams();

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [fetchStreams]);

  return {
    liveStreams,
    pastStreams,
    loading,
    error,
    refresh: fetchStreams,
  };
}

export default useLiveStreamers;
